<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { inventoryApi } from '@/api/inventory';
import { useMobile } from '@/composables/useMobile';
import InventoryPlaceStock, { type PlaceAction } from './inventory-place-stock.vue';

// Stock of one warehouse, with the same items/history layout as a patient's
// home: deliveries to patients, transfers between warehouses, entries, use,
// counts, returns and losses.
const { isPhone } = useMobile();

const { data: warehouses } = useQuery({
  queryKey: ['inventoryLocations', 'warehouses'],
  queryFn: () => inventoryApi.locations({ kinds: ['warehouse'] }),
  initialData: [],
});

const warehouseId = ref<number | undefined>();
watch(warehouses, (list) => {
  if (!warehouseId.value && list.length) warehouseId.value = list[0].id;
}, { immediate: true });
const place = computed(() => ({ locationId: warehouseId.value }));

const BALANCE_ACTIONS: PlaceAction[] = [
  { mode: 'delivery', title: 'Entregar', primary: true },
  { mode: 'use', title: 'Registrar uso' },
  { mode: 'count', title: 'Ajustar por conteo' },
  { mode: 'transfer', title: 'Trasladar a otra bodega' },
  { mode: 'return', title: 'Devolver al proveedor' },
  { mode: 'loss', title: 'Pérdida o baja' },
];
const UNIT_ACTIONS: PlaceAction[] = [
  { mode: 'delivery', title: 'Entregar', primary: true },
  { mode: 'transfer', title: 'Trasladar a otra bodega' },
  { mode: 'return', title: 'Devolver' },
  { mode: 'loss', title: 'Dar de baja' },
];
</script>
<template>
  <div class="pa-4 d-flex flex-column ga-4">
    <p
      v-if="warehouses.length === 0"
      class="text-body-2 text-medium-emphasis"
    >
      La empresa no tiene bodegas: el stock vive en el domicilio de cada paciente.
      Si guardan stock propio, créala en «Configuración».
    </p>
    <template v-else>
      <v-select
        v-model="warehouseId"
        :items="warehouses"
        item-title="name"
        item-value="id"
        label="Bodega"
        variant="outlined"
        density="compact"
        hide-details
        :class="{ 'warehouse-select': !isPhone }"
      />
      <inventory-place-stock
        v-if="warehouseId"
        :key="warehouseId"
        :place="place"
        :balance-actions="BALANCE_ACTIONS"
        :unit-actions="UNIT_ACTIONS"
        empty-text="Sin artículos en esta bodega"
      />
    </template>
  </div>
</template>
<style scoped>
.warehouse-select {
  max-width: 320px;
}
</style>
